import type { MenuItem } from "@v1/components/designSystem/menu/Menu";
import type { ReploElement } from "schemas/generated/element";

import * as React from "react";
import { useEffect } from "react";

import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import Input from "@v1/components/common/designSystem/Input";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { Menu } from "@v1/components/designSystem/menu/Menu";
import Popover from "@v1/components/designSystem/popover/Popover";
import { useUnarchiveElement } from "@v1/hooks/useUnarchiveElement";
import {
  selectEditorMode,
  selectSelectedArchivedElementId,
  setEditorMode,
  setSelectedArchivedElementId,
} from "@v1/reducers/core-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useOpenModal } from "@v2/store/modalStore";
import { elementRouter } from "@v2/trpc/routes/element";
import {
  AlignVerticalSpaceAround,
  AppWindow,
  Check,
  Ellipsis,
  Filter,
  Newspaper,
  Search,
  ShoppingCart,
  X,
} from "lucide-react";

import { formatVersionHistoryDateAndTime } from "../version-history/VersionCard";

function getIcon(type: string) {
  switch (type) {
    case "page":
      return <AppWindow className="text-default" size={16} />;
    case "shopifySection":
      return <AlignVerticalSpaceAround className="text-default" size={16} />;
    case "shopifyProductTemplate":
      return <ShoppingCart className="text-default" size={16} />;
    case "shopifyArticle":
      return <Newspaper className="text-default" size={16} />;
    default:
      return null;
  }
}

type SortBy = "dateArchived" | "alphabetical" | "dateCreated";
type SortOrder = "desc" | "asc";
type SortConfig = { sortBy: SortBy; sortOrder: SortOrder };

const SORT_OPTIONS = [
  { label: "Date Archived", value: "dateArchived" as const },
  { label: "Alphabetical", value: "alphabetical" as const },
  { label: "Date Created", value: "dateCreated" as const },
];

function sortElements(
  elements: ReploElement[],
  { sortBy, sortOrder }: SortConfig,
) {
  const sortFns: Record<SortBy, (a: ReploElement, b: ReploElement) => number> =
    {
      dateArchived: (a, b) => {
        return (
          new Date(a.archivedAt!).getTime() - new Date(b.archivedAt!).getTime()
        );
      },
      alphabetical: (a, b) => a.name.localeCompare(b.name),
      dateCreated: (a, b) => {
        return (
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      },
    };

  return [...elements].sort((a, b) => {
    const comparison = sortFns[sortBy](a, b);
    return sortOrder === "desc" ? -comparison : comparison;
  });
}

function filterElements(elements: ReploElement[], searchTerm: string) {
  if (!searchTerm) {
    return elements;
  }

  const lowerSearchTerm = searchTerm.toLowerCase();
  return elements.filter((element) => {
    return (
      element.name.toLowerCase().includes(lowerSearchTerm) ||
      element.type.toLowerCase().includes(lowerSearchTerm)
    );
  });
}

type ArchivedElementsState = {
  searchTerm: string;
  sortConfig: {
    sortBy: SortBy;
    sortOrder: SortOrder;
  };
  selectedElementId: string | null;
};

type ArchivedElementsAction =
  | { type: "SET_SEARCH_TERM"; payload: string }
  | { type: "SET_SORT_BY"; payload: SortBy }
  | { type: "SET_SORT_ORDER"; payload: SortOrder }
  | { type: "SET_SELECTED_ELEMENT"; payload: string | null };

function archivedElementsReducer(
  state: ArchivedElementsState,
  action: ArchivedElementsAction,
): ArchivedElementsState {
  switch (action.type) {
    case "SET_SEARCH_TERM":
      return { ...state, searchTerm: action.payload };
    case "SET_SORT_BY":
      return {
        ...state,
        sortConfig: { ...state.sortConfig, sortBy: action.payload },
      };
    case "SET_SORT_ORDER":
      return {
        ...state,
        sortConfig: { ...state.sortConfig, sortOrder: action.payload },
      };
    case "SET_SELECTED_ELEMENT":
      return { ...state, selectedElementId: action.payload };
    default:
      return state;
  }
}

export default function ArchivedElementsPane() {
  const dispatch = useEditorDispatch();
  const projectId = useCurrentProjectId();
  const { data: elements = [], isLoading } =
    elementRouter.listArchived.useQuery(projectId ? { projectId } : skipToken);
  const [state, dispatchReducer] = React.useReducer(archivedElementsReducer, {
    searchTerm: "",
    sortConfig: {
      sortBy: "dateArchived",
      sortOrder: "desc",
    },
    selectedElementId: null,
  });
  const editorMode = useEditorSelector(selectEditorMode);
  const openModal = useOpenModal();
  const selectedArchivedElementId = useEditorSelector(
    selectSelectedArchivedElementId,
  );
  const { unarchiveElement } = useUnarchiveElement();

  const filteredAndSortedElements = React.useMemo(() => {
    const filtered = filterElements(elements, state.searchTerm);
    return sortElements(filtered, state.sortConfig);
  }, [elements, state.searchTerm, state.sortConfig]);

  const onElementClick = (elementId: string) => {
    dispatch(setSelectedArchivedElementId(elementId));
    dispatchReducer({ type: "SET_SELECTED_ELEMENT", payload: elementId });
  };

  const handleUnarchive = React.useCallback(
    (element: ReploElement) => {
      unarchiveElement(element, { from: "pane" });
    },
    [unarchiveElement],
  );

  const getMenuItems = React.useCallback(
    (element: ReploElement): MenuItem[] => {
      return [
        {
          type: "leaf",
          id: "unarchive",
          title: "Unarchive",
          variant: "default",
          size: "sm",
          onSelect: () => handleUnarchive(element),
          disabled: false,
        },
        {
          type: "leaf",
          id: "delete",
          title: "Delete Forever",
          variant: "default",
          size: "sm",
          onSelect: () => {
            return openModal({
              type: "permanentlyDeleteElementModal",
              props: { element },
            });
          },
          disabled: false,
        },
      ];
    },
    [handleUnarchive, openModal],
  );

  useEffect(() => {
    if (editorMode !== "archived") {
      dispatch(setSelectedArchivedElementId(null));
      dispatchReducer({ type: "SET_SELECTED_ELEMENT", payload: null });
    }
  }, [editorMode, dispatch]);

  useEffect(() => {
    const firstElement = elements[0];
    if (
      editorMode === "archived" &&
      elements.length > 0 &&
      !state.selectedElementId
    ) {
      if (selectedArchivedElementId) {
        const elementExists = elements.some(
          (e) => e.id === selectedArchivedElementId,
        );
        if (elementExists) {
          dispatchReducer({
            type: "SET_SELECTED_ELEMENT",
            payload: selectedArchivedElementId,
          });
        } else {
          if (firstElement) {
            dispatch(setSelectedArchivedElementId(firstElement.id));
            dispatchReducer({
              type: "SET_SELECTED_ELEMENT",
              payload: firstElement.id,
            });
          }
        }
      } else {
        if (firstElement) {
          dispatch(setSelectedArchivedElementId(firstElement.id));
          dispatchReducer({
            type: "SET_SELECTED_ELEMENT",
            payload: firstElement.id,
          });
        }
      }
    }
  }, [
    elements,
    state.selectedElementId,
    dispatch,
    editorMode,
    selectedArchivedElementId,
  ]);

  return (
    editorMode === "archived" && (
      <div className="border-r-0.5 fixed left-0 top-[61px] h-[calc(100%-60px)] w-[268px] bg-white">
        <div className="relative flex h-full w-full flex-col">
          <div className="z-max border-border fixed flex w-[268px] flex-col border-b bg-white px-3 py-3">
            <div className="flex items-center justify-between">
              <h2 className="text-default text-sm font-semibold">Archived</h2>
              <DeprecatedIconButton
                variant="tertiary"
                size="sm"
                tooltipText="Close"
                onClick={() => dispatch(setEditorMode("edit"))}
                icon={<X size={16} />}
              />
            </div>
            <div className="mt-3 flex gap-2">
              <Input
                autoComplete="off"
                placeholder="Search"
                layoutClassName="grow"
                value={state.searchTerm}
                onChange={(e) => {
                  return dispatchReducer({
                    type: "SET_SEARCH_TERM",
                    payload: e.target.value,
                  });
                }}
                startEnhancer={<Search size={14} />}
              />
              <SortMenu
                sortBy={state.sortConfig.sortBy}
                setSortBy={(sortBy) => {
                  return dispatchReducer({
                    type: "SET_SORT_BY",
                    payload: sortBy,
                  });
                }}
                sortOrder={state.sortConfig.sortOrder}
                setSortOrder={(sortOrder) => {
                  return dispatchReducer({
                    type: "SET_SORT_ORDER",
                    payload: sortOrder,
                  });
                }}
              />
            </div>
          </div>
          <div className="no-scrollbar mt-24 h-full overflow-y-scroll">
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <Spinner variant="primary" className="size-[33px]" />
              </div>
            ) : (
              <div className="relative flex flex-col gap-1 pl-3 pr-2">
                {filteredAndSortedElements.map((element) => (
                  <div
                    className={twMerge(
                      "hover:bg-sheet group flex h-12 cursor-pointer flex-row items-center justify-between rounded-sm p-2",
                      selectedArchivedElementId === element.id &&
                        "bg-selectable-selected hover:bg-selectable-selected",
                    )}
                    onClick={() => onElementClick(element.id)}
                    key={element.id}
                  >
                    <div className="flex flex-col items-start gap-px">
                      <div className="flex items-center gap-2">
                        <div className="w-4 pt-px">{getIcon(element.type)}</div>
                        <p className="text-default block w-44 overflow-hidden truncate text-sm">
                          {element.name}
                        </p>
                      </div>
                      <p className="text-muted pl-6 text-sm">
                        {element?.archivedAt
                          ? formatVersionHistoryDateAndTime(element.archivedAt)
                          : "N/A"}
                      </p>
                    </div>
                    <Menu
                      items={getMenuItems(element)}
                      menuType="normal"
                      customWidth="auto"
                      side="right"
                      trigger={
                        <DeprecatedIconButton
                          icon={<Ellipsis size={12} className="text-muted" />}
                          variant="link"
                          tooltipText="More"
                        />
                      }
                      disableTriggerFocusOnClose
                    />
                  </div>
                ))}
              </div>
            )}
            {filteredAndSortedElements.length === 0 && !isLoading && (
              <div className="flex h-full items-center justify-center">
                <p className="text-muted text-sm">
                  {elements.length === 0
                    ? "No archived elements found"
                    : `No results found for "${state.searchTerm}"`}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  );
}

const SortMenu: React.FC<{
  sortBy: SortBy;
  setSortBy: (value: SortBy) => void;
  sortOrder: SortOrder;
  setSortOrder: (value: SortOrder) => void;
}> = ({ sortBy, setSortBy, sortOrder, setSortOrder }) => {
  return (
    <Popover.Root>
      <Popover.Trigger>
        <DeprecatedIconButton
          icon={<Filter size={16} />}
          tooltipText="Filter"
          variant="secondary"
          layoutClassName="h-full"
        />
      </Popover.Trigger>
      <Popover.Content
        className="w-[200px] p-2"
        shouldPreventDefaultOnInteractOutside
        hideCloseButton
      >
        <div className="flex flex-col gap-2 p-1">
          <div className="flex flex-col gap-1">
            <span className="text-default text-xs font-semibold">Sort by</span>
            <div className="flex flex-col gap-1">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={twMerge(
                    "text-default hover:bg-sheet flex items-center justify-between rounded-sm px-2 py-1 text-left text-sm",
                    sortBy === option.value &&
                      "bg-selectable-selected hover:bg-selectable-selected",
                  )}
                  onClick={() => setSortBy(option.value)}
                >
                  {option.label}
                  {sortBy === option.value && (
                    <Check className="text-default" size={16} />
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-default text-xs font-semibold">
              Sort Order
            </span>
            <div className="flex flex-col gap-1">
              <button
                className={twMerge(
                  "text-default hover:bg-sheet flex items-center justify-between rounded-sm px-2 py-1 text-left text-sm",
                  sortOrder === "desc" &&
                    "bg-selectable-selected hover:bg-selectable-selected",
                )}
                onClick={() => setSortOrder("desc")}
              >
                {sortBy === "alphabetical" ? "Descending" : "Newest First"}

                {sortOrder === "desc" && (
                  <Check className="text-default" size={16} />
                )}
              </button>
              <button
                className={twMerge(
                  "text-default hover:bg-sheet flex items-center justify-between rounded-sm px-2 py-1 text-left text-sm",
                  sortOrder === "asc" &&
                    "bg-selectable-selected hover:bg-selectable-selected",
                )}
                onClick={() => setSortOrder("asc")}
              >
                {sortBy === "alphabetical" ? "Ascending" : "Oldest First"}
                {sortOrder === "asc" && (
                  <Check className="text-default" size={16} />
                )}
              </button>
            </div>
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};
